import React, { memo, useCallback, type ReactNode } from 'react';
import { styled as styled2 } from '@compiled/react';
import isNil from 'lodash/isNil';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { useFieldValue } from '@atlassian/jira-issue-field-base/src/services/field-value-service/index.tsx';
import { IssueOriginalEstimateFieldBoundary } from '@atlassian/jira-issue-field-original-estimate/src/ui/async.tsx';
import type IssueOriginalEstimateFieldType from '@atlassian/jira-issue-field-original-estimate/src/ui/index.tsx';
import { TimeEstimateKey } from '@atlassian/jira-providers-issue/src/model/issue-system-fields.tsx';
import { useShouldRefetchEpicPanel } from '@atlassian/jira-software-backlog-panel/src/services/epic-panel-refetch/index.tsx';
import {
	timeTrackingFormatterLongForm,
	timeTrackingFormatter,
} from '@atlassian/jira-time-tracking-formatter/src/main.tsx';
import type { TimeTrackingOptions } from '@atlassian/jira-time-tracking-formatter/src/types.tsx';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
import { BACKLOG_PACKAGE_NAME } from '../../../../../common/constants/analytics.tsx';
import { ORIGINAL_ESTIMATE } from '../../../../../model/constants.tsx';
import type { EstimationType } from '../../../../../model/types.tsx';
import {
	cancelInlineEditOriginalEstimateInteractionMark,
	setInlineEditOriginalEstimateInteractionStartMark,
	setInlineEditOriginalEstimateInteractionStopMark,
} from '../../../../../services/performance/interaction-metric-inline-edit.tsx';
import { refetchVersions } from '../../../../../state/actions/version/index.tsx';
import { useBacklogDispatch, useBacklogSelector } from '../../../../../state/index.tsx';
import { getCards } from '../../../../../state/selectors/cards/index.tsx';
import { useIsCMPBacklog } from '../../../../../state/state-hooks/capabilities.tsx';
import SLAExperienceTracker from '../../../../sla-experience-tracker/index.tsx';
import { CARD_CONTAINER_SELECTOR } from '../../constants.tsx';
import EstimateFieldWrapper from '../estimate-field-wrapper/index.tsx';
import messages from './messages.tsx';
import OriginalEstimateFieldStatic from './original-estimate-field-static/index.tsx';
import { timeTrackingConfigTransformer } from './utils.tsx';

const IssueOriginalEstimateField = lazyForPaint<typeof IssueOriginalEstimateFieldType>(
	() =>
		import(
			/* webpackChunkName: "async-issue-field-original-estimate" */ '@atlassian/jira-issue-field-original-estimate'
		),
);

export type Props = {
	issueId: number;
	issueKey: string;
	estimationType: EstimationType;
	timeTrackingOptions: TimeTrackingOptions;
	isUserAtLeastMember: boolean;
	shouldRenderRichCard: boolean;
	shouldRenderEstimatesColumn: boolean;
};

const OriginalEstimateField = ({
	issueId,
	issueKey,
	estimationType,
	timeTrackingOptions,
	isUserAtLeastMember,
	shouldRenderRichCard,
	shouldRenderEstimatesColumn,
}: Props) => {
	const isCompanyManaged = useIsCMPBacklog();
	const hasEstimate = useBacklogSelector((state) => !!getCards(state)[String(issueId)]?.estimate);
	const dispatch = useBacklogDispatch();

	const [_, { triggerEpicPanelRefetch }] = useShouldRefetchEpicPanel();

	const intl = useIntl();
	const [originalEstimateFieldValue] = useFieldValue({
		issueKey,
		fieldKey: TimeEstimateKey,
	});

	const handleConfirm = useCallback(() => {
		setInlineEditOriginalEstimateInteractionStartMark();
	}, []);

	const handleUpdate = useCallback(() => {
		setInlineEditOriginalEstimateInteractionStopMark(isCompanyManaged);
		dispatch(refetchVersions());
		if (isCompanyManaged) {
			triggerEpicPanelRefetch();
		}
	}, [dispatch, isCompanyManaged, triggerEpicPanelRefetch]);

	const handleFailure = useCallback(() => {
		cancelInlineEditOriginalEstimateInteractionMark();
	}, []);

	if (!shouldRenderEstimatesColumn && (estimationType !== ORIGINAL_ESTIMATE || !hasEstimate)) {
		return null;
	}

	const isOriginalEstimateAccessible = !isNil(originalEstimateFieldValue);
	const value = isOriginalEstimateAccessible
		? timeTrackingFormatter(originalEstimateFieldValue, timeTrackingOptions, intl)
		: undefined;

	const placeholder = '2w 4d 6h 45m';
	const invalidTimeFormatMessage = (
		<FormattedMessage
			{...messages.invalidTimeFormatMessage}
			values={{ format: <b>{placeholder}</b> }}
		/>
	);

	const longFormValue =
		isOriginalEstimateAccessible && originalEstimateFieldValue > 0
			? timeTrackingFormatterLongForm(originalEstimateFieldValue, timeTrackingOptions, intl)
			: undefined;

	const ariaLabel = longFormValue
		? `${intl.formatMessage(messages.originalEstimateAriaLabel)}: ${longFormValue}`
		: `${intl.formatMessage(messages.originalEstimateAriaLabel)}: ${intl.formatMessage(messages.originalEstimateAriaLabelNullValue)}`;

	const issueOriginalEstimateFieldStatic = (
		<OriginalEstimateFieldStatic
			value={value}
			valueInSeconds={originalEstimateFieldValue}
			hasEstimate={hasEstimate}
			timeTrackingOptions={timeTrackingOptions}
		/>
	);

	if (isUserAtLeastMember && shouldRenderRichCard) {
		return (
			<IssueOriginalEstimateFieldBoundary
				packageName={BACKLOG_PACKAGE_NAME}
				fallback={issueOriginalEstimateFieldStatic}
			>
				<SLAExperienceTracker
					experienceTarget="inline original estimate"
					experienceTargetId={issueKey}
					additionalAttributes={{ issueKey }}
				>
					<OriginalEstimateFieldWrapper shouldRenderEstimatesColumn={shouldRenderEstimatesColumn}>
						{hasEstimate && (
							<OriginalEstimateFieldContainer hasValue={isOriginalEstimateAccessible}>
								<EstimateFieldWrapper
									tooltipLabel={intl.formatMessage(messages.originalEstimateTooltip)}
									ariaLabel={ariaLabel}
								>
									<IssueOriginalEstimateField
										isEditable
										isMobile={false}
										isFitContainerWidthReadView
										fieldId={TimeEstimateKey}
										issueId={String(issueId)}
										issueKey={issueKey}
										config={timeTrackingConfigTransformer(timeTrackingOptions)}
										placeholder={placeholder}
										timeTrackingValue={{}} // NOTE: time tracking is irrelevant in Backlog inline original estimate
										invalidTimeFormatMessage={invalidTimeFormatMessage}
										isBacklogComponent
										onUpdate={handleUpdate}
										onConfirm={handleConfirm}
										onFailure={handleFailure}
									/>
								</EstimateFieldWrapper>
							</OriginalEstimateFieldContainer>
						)}
					</OriginalEstimateFieldWrapper>
				</SLAExperienceTracker>
			</IssueOriginalEstimateFieldBoundary>
		);
	}

	return issueOriginalEstimateFieldStatic;
};

export default memo<Props>(OriginalEstimateField);

type OriginalEstimateFieldProps = {
	hasValue: boolean;
	children: ReactNode;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const OriginalEstimateFieldContainer = styled2.div<OriginalEstimateFieldProps>({
	/* NOTE: cursor stylings for hover behavior should be handled inside the field component, not here */
	cursor: 'default',

	/* build our own focus effect using box-shadow because AK's focus takes more space and doesn't respond to active state */
	'&:focus, &:focus-within': {
		outline: 'none',
	},

	/*
	 * NOTE: The AK component used by AsyncIssueOriginalEstimateField is
	 * from an older version. The following styling adjustments may be
	 * subject to change/remove after AK components are bumped in BENTO.
	 */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	div: {
		/*
		 * AK badge has several inline-block elements which result in
		 * misalignment, so we override display: inline-block to display: flex
		 */
		display: 'flex',
		/*
		 * NOTE: This is used to remove the focus blue ring around an
		 * invisible element inside AK component (most likely a screen
		 * reader label) when the story point component is focussed and
		 * hovered at the same time
		 */
		'&:empty': {
			boxShadow: 'none',
		},
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > div > div': {
		/* remove a margin-left: -8px offset from AK badge */
		marginLeft: 0,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		"&[data-component-selector='jira-issue-field-inline-edit-readonly-view-container']": {
			/*
			 * NonEditableMargin overrides.
			 * The top margin here negates the negative margin being applied to the parent (where it's used to create extra space for the input element).
			 * This component's original margin is intended for use within bento. So, we set all the other values to 0 too.
			 */

			marginTop: token('space.100'),
			marginRight: 0,
			marginBottom: 0,
			marginLeft: 0,
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& > div > div': {
			/* AK badge has a higher precedence display: inline-block on this level, so we override it specifically here */
			display: 'flex',
		},
	},

	/* undo hide if card is focused or hovered */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	[`[data-component-selector="${CARD_CONTAINER_SELECTOR}"]:hover &, ` +
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	`[data-component-selector="${CARD_CONTAINER_SELECTOR}"]:active &, ` +
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	`[data-component-selector="${CARD_CONTAINER_SELECTOR}"]:focus &, ` +
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	`[data-component-selector="${CARD_CONTAINER_SELECTOR}"]:focus-within &`]: {
		position: 'inherit',
		opacity: 'inherit',
		pointerEvents: 'inherit',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	minWidth: `${gridSize * 10}px`,
	overflow: 'inherit',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	"[data-testid^='issue-field-original-estimate.ui.view.badge']": {
		display: 'block',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		maxWidth: `${gridSize * 10}px`,
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > span': {
			display: 'block',
			overflow: 'hidden',
			whiteSpace: 'nowrap',
			textOverflow: 'ellipsis',
		},
	},

	display: 'flex',
	height: '100%',
	alignItems: 'center',
});

// empty column for the cards that without original estimates
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OriginalEstimateFieldWrapper = styled2.div<{
	shouldRenderEstimatesColumn: boolean;
}>(
	{ display: 'flex', alignSelf: 'stretch', alignItems: 'center' },
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) =>
		props.shouldRenderEstimatesColumn && {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			minWidth: `${gridSize * 10}px`,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			maxWidth: `${gridSize * 10}px`,
		},
);
